/**
 * This file was auto-generated by client-types. Do not manually edit this file.
 * Your changes would be automatically overwritten.
 *
 * Service Documentation: https://private.hubteam.com/shoebill/service/com.hubspot.applications.rpc.ApplicationsRpc
 */

import type {
  ChirpAvroRpcDetails,
  Requestify,
  unhandledEnumCase,
  Default,
  Derived,
  HubspotMetadata,
} from 'rpc-client-utils';

/**
 *  A single entry of {@code ApplicationResponseExtras#getAppPermissions()} — the same (id, name)
 *  pair as {@link ScopeGroupRefIF}, plus the REQUIRED/ADDITIONAL/OPTIONAL type, so this carries
 *  every field {@link ScopeGroupRefIF}/{@link ApplicationScopeGroupsIF} do.
 */
export type AppPermissionDetail = {
  id: number;
  name: string;
  appPermissionType: AppPermissionType;
};

export type ApplicationAppClientInfo = {
  clientId?: string;
  componentType: string;
  authConfigType: string;
};

/**
 *  Application domain information containing domain and verification status.
 *  Used to encapsulate domain data for applications.
 */
export type ApplicationDomainInfo = { domain?: string; verified: boolean };

export type ApplicationInstallCounts = {
  uniquePortalInstallCount: Default<number>;
  uniqueUserInstallCount: Default<number>;
};

/**
 *  Application listing information from the marketplace/ecosystem service.
 *  Contains metadata about an app's marketplace listing status.
 */
export type ApplicationListingInfo = {
  listingUrl?: string;
  isCertified: boolean;
  isPublished: boolean;
  hasDraft: boolean;
  inReview: boolean;
  privacyUrl?: string;
  termsUrl?: string;
};

/**
 *  Where the app's owning portal lives, and what kind of portal it is. Raw hublet/region
 *  strings and the Hubs hub type id/name, since those vocabularies are owned elsewhere.
 */
export type ApplicationPortalInfo = {
  portalId: number;
  hublet: string;
  region?: string;
  deployState?: string;
  portalTypeId?: number;
  portalType?: string;
  active?: boolean;
  purged?: boolean;
  suspended?: boolean;
  fullyTrusted?: boolean;
  friendsAndFamily?: boolean;
  createdAt?: number;
  accountName?: string;
  domain?: string;
};

/**
 *  Application project information containing project ID and name.
 *  Used to encapsulate project association data for applications.
 */
export type ApplicationProjectInfo = {
  projectId?: number;
  projectName?: string;
};

/**
 *  This immutable combines ApplicationCore with additional fields stored
 *  in the Applications tablespace, such as scope groups.
 */
export type ApplicationResponse = {
  /** @deprecated use ApplicationResponseExtras#getRedirectUrls() from getApplicationResponseExtras() instead. */
  redirectUrls: Default<string[]>;
  /** @deprecated use ApplicationResponseExtras#getAppPermissions() from getApplicationResponseExtras() instead. */
  scopeGroups: ApplicationScopeGroups;
  /** @deprecated use ApplicationResponseExtras#getPermittedUrls() from getApplicationResponseExtras() instead. */
  permittedUrls?: PermittedUrls;
  /** @deprecated use ApplicationResponseExtras#getAppComponentIndex() from getApplicationResponseExtras() instead. */
  appComponentIndex?: AppComponentIndex;
  applicationResponseExtras: Default<ApplicationResponseExtras>;
  id: number;
  name: string;
  description?: string;
  portalId: number;
  updatedAt: number;
  createdAt: number;
  clientId: string;
  iconUrl?: string;
  archived: Default<boolean>;
  ownerId?: number;
  supportInfo?: ApplicationSupportInfo;
  isUserLevel: boolean;
  isBusinessUnitEnabled: Default<boolean>;
  isFeatured: boolean;
  isInternal: boolean;
  installsCanSelectAllOptionalScopes: Default<boolean>;
  installsCanReplaceConditionalScopes: Default<boolean>;
  authType?: AuthType;
  distributionType?: DistributionType;
  acceptableUsePolicyStatus?: AcceptableUsePolicyStatus;
  buildSource?: BuildSource;
  buildPlatformTag: Default<BuildPlatformTag>;
  appType?: AppType;
  appOrigin?: AppOrigin;
  isClapManaged: Default<boolean>;
};

export type ApplicationScopeGroups = {
  requiredScopeGroupInfo: Default<ScopeGroupRef[]>;
  conditionallyRequiredScopeGroupInfo: Default<ScopeGroupRef[]>;
  optionalScopeGroupInfo: Default<ScopeGroupRef[]>;
};

export type ApplicationSupportInfo = {
  supportEmail?: string;
  documentationUrl?: string;
  supportUrl?: string;
  supportPhoneNumber?: string;
};

export type PermittedUrls = {
  fetch: Default<string[]>;
  iframe: Default<string[]>;
  img: Default<string[]>;
};

export type ScopeGroupRef = { id: number; name: string };

export type AcceptableUsePolicyStatus =
  | 'SIGNED'
  | 'UNSIGNED'
  | unhandledEnumCase;

export type AppExtraInfoStatus =
  | 'NOT_REQUESTED'
  | 'FOUND'
  | 'NOT_FOUND'
  | 'ONLY_APPLICABLE_TO_CLAP_MANAGED_APPS'
  | 'NOT_IMPLEMENTED_FOR_BATCH_REQUESTS'
  | 'SYNTHETICALLY_GENERATED'
  | 'FOUND_VIA_LEGACY_APP_TO_SCOPE_GROUPS'
  | 'FOUND_VIA_CLAP'
  | 'REDIRECT_URLS_NOT_SUPPORTED_FOR_CLAP_MANAGED_APPS'
  | unhandledEnumCase;

export type AppFilter =
  | 'UNIFIED'
  | 'MCP_CONNECTOR'
  | 'LEGACY_PUBLIC'
  | 'LEGACY_PRIVATE'
  | 'SERVICE_KEY'
  | 'CAN_MIGRATE_TO_UNIFIED'
  | unhandledEnumCase;

export type AppOrigin =
  | 'LEGACY_PUBLIC_UI'
  | 'LEGACY_PUBLIC_PROJECT'
  | 'UNIFIED'
  | 'LEGACY_PRIVATE_UI'
  | 'LEGACY_PRIVATE_PROJECT'
  | 'MCP_CONNECTOR_UI'
  | 'API_KEY'
  | 'SERVICE_KEY'
  | unhandledEnumCase;

export type AppType =
  | 'LEGACY_PUBLIC_UI'
  | 'LEGACY_PUBLIC_PROJECT'
  | 'UNIFIED'
  | 'LEGACY_PRIVATE_UI'
  | 'LEGACY_PRIVATE_PROJECT'
  | 'MCP_CONNECTOR_UI'
  | 'API_KEY'
  | 'SERVICE_KEY'
  | unhandledEnumCase;

export type AuthType = 'OAUTH' | 'STATIC' | 'NO_API_ACCESS' | unhandledEnumCase;

export type BuildPlatformTag =
  | 'UNKNOWN'
  | 'BREEZE'
  | 'LOVABLE'
  | unhandledEnumCase;

export type BuildSource = 'HUBSPOT' | 'THIRD_PARTY' | unhandledEnumCase;

export type DistributionType = 'MARKETPLACE' | 'PRIVATE' | unhandledEnumCase;

export type MigrationEligibility =
  | 'MIGRATABLE'
  | 'UNMIGRATABLE_PRIVATE_APP'
  | 'UNMIGRATABLE_INELIGIBLE_APP_ORIGIN'
  | 'UNMIGRATABLE_ALREADY_MIGRATED'
  | 'UNMIGRATABLE_ALREADY_UNIFIED_APP'
  | 'UNMIGRATABLE_UNLISTED_APP_OVER_THE_PRIVATE_INSTALL_LIMIT'
  | 'UNMIGRATABLE_APP_ARCHIVED'
  | unhandledEnumCase;

export type ApplicationError = {
  errorType: ApplicationErrorType;
  chirpErrorMessage?: string;
  coordinates?: ChirpMethodCoordinates;
  causalChain: Default<CausalChainLink[]>;
  shouldRetry: Default<boolean>;
};

export type ApplicationErrorType =
  | 'APP_NOT_FOUND'
  | 'APP_NOT_OWNED_BY_PORTAL'
  | 'APP_COMPONENT_INDEX_NOT_FOUND'
  | 'REVISION_NOT_FOUND'
  | 'INTERNAL_ERROR'
  | 'INCOMPATIBLE_APP_FILTERS'
  | unhandledEnumCase;

export type AiConnectorCatalogCursor = { offset: number };

export type AiConnectorCatalogRequest = {
  pageLength: Default<number>;
  offset: AiConnectorCatalogCursor;
};

export type AiConnectorCatalogResponse = {
  total: number;
  results: Default<AppNameAndLogo[]>;
  hasMore: boolean;
  offset: AiConnectorCatalogCursor;
};

export type AppComponentIndex = {
  appId: number;
  projectName: string;
  portalId: number;
  developerSymbol: string;
  /** @deprecated This field is not guaranteed to be populated in return values, and should not be used */
  latestRevisionId: string;
  liveRevisionId?: string;
  latestRevisionIdUuid?: Derived<string>;
};

export type AppComponentIndexByAppIdRequest = {
  /** @deprecated Use getAppId() instead. */
  applicationId?: number;
  appId: number;
};

export type AppComponentIndexResponse = {
  /** @deprecated Use getAppId() instead. */
  applicationId: number;
  appId: number;
  appComponentIndex?: AppComponentIndex;
};

export type AppNameAndLogo = {
  appId: number;
  appName: string;
  appLogo?: string;
};

export type ApplicationByClientIdRequest = {
  clientId: string;
  applicationRequestExtras: Default<ApplicationRequestExtras>;
};

export type ApplicationByIdRequest = {
  appId: number;
  applicationRequestExtras: Default<ApplicationRequestExtras>;
};

export type ApplicationRequestExtras = {
  includeProjectAssociation: Default<boolean>;
  includeInstallCounts: Default<boolean>;
  includeCachedInstallCounts: Default<boolean>;
  includeListingInfo: Default<boolean>;
  includeDomainInfo: Default<boolean>;
  includeMigrationEligibility: Default<boolean>;
  includeRedirectUrls: Default<boolean>;
  /** @deprecated use getIncludeAppPermissions() instead. */
  includeScopeGroups: Default<boolean>;
  includePermittedUrls: Default<boolean>;
  includeAppComponentIndex: Default<boolean>;
  includeProjectId: Default<boolean>;
  includeAppAuthInfo: Default<boolean>;
  includeAppPermissions: Default<boolean>;
  includePortalInfo: Default<boolean>;
  useCachedListingInfo: Default<boolean>;
};

export type ApplicationResponseExtras = {
  projectAssociation?: ApplicationProjectInfo;
  projectAssociationStatus: Default<AppExtraInfoStatus>;
  installCounts?: ApplicationInstallCounts;
  installCountsStatus: Default<AppExtraInfoStatus>;
  cachedInstallCounts?: ApplicationInstallCounts;
  cachedInstallCountsStatus: Default<AppExtraInfoStatus>;
  listingInfo?: ApplicationListingInfo;
  listingInfoStatus: Default<AppExtraInfoStatus>;
  domainInfo?: ApplicationDomainInfo;
  domainInfoStatus: Default<AppExtraInfoStatus>;
  migrationEligibility?: MigrationEligibility;
  migrationEligibilityStatus: Default<AppExtraInfoStatus>;
  redirectUrls?: string[];
  redirectUrlsStatus: Default<AppExtraInfoStatus>;
  /** @deprecated use getAppPermissions() instead. */
  scopeGroups?: ApplicationScopeGroups;
  /** @deprecated use getAppPermissionsStatus() instead. */
  scopeGroupsStatus: Default<AppExtraInfoStatus>;
  permittedUrls?: PermittedUrls;
  permittedUrlsStatus: Default<AppExtraInfoStatus>;
  appComponentIndex?: AppComponentIndex;
  appComponentIndexStatus: Default<AppExtraInfoStatus>;
  projectId?: number;
  projectIdStatus: Default<AppExtraInfoStatus>;
  appClientInfo?: ApplicationAppClientInfo[];
  appClientInfoStatus: Default<AppExtraInfoStatus>;
  appPermissions?: AppPermissionDetail[];
  appPermissionsStatus: Default<AppExtraInfoStatus>;
  portalInfo?: ApplicationPortalInfo;
  portalInfoStatus: Default<AppExtraInfoStatus>;
};

export type ApplicationsByIdsRequest = {
  appIds: Default<number[]>;
  applicationRequestExtras: Default<ApplicationRequestExtras>;
};

export type ApplicationsByIdsResponse = {
  applications: Default<ApplicationResponse[]>;
};

/**
 *  Request for getting applications by portalId with optional filtering.
 */
export type ApplicationsByPortalRequest = {
  appFilters: Default<AppFilter[]>;
  applicationRequestExtras: Default<ApplicationRequestExtras>;
};

/**
 *  Response containing applications filtered by origin.
 */
export type ApplicationsByPortalResponse = {
  applications: Default<ApplicationResponse[]>;
};

export type ApplicationsByProjectAndPortalResponse = {
  applications: Default<ApplicationResponse[]>;
};

export type ApplicationsByProjectRequest = {
  projectName: string;
  requestExtras: Default<ApplicationRequestExtras>;
};

export type GetLocalisedAppPermissionsRequest = { appId: number };

export type GetLocalisedAppPermissionsResponse = {
  appId: number;
  requiredAppPermissions: Default<LocalisedAppPermission[]>;
  conditionallyRequiredAppPermissions: Default<LocalisedAppPermission[]>;
  optionalAppPermissions: Default<LocalisedAppPermission[]>;
};

/**
 *  App-auth request for looking up an app <em>owned by the calling portal</em>, by its developer
 *  coordinates. Unlike {@link ApplicationByComponentKeyRequestIF}, this request deliberately omits
 *  the portal id: the APP gateway derives the authenticated portal from the ChirpContext, so the
 *  lookup is inherently scoped to apps the caller owns and callers only supply the project name and
 *  developer symbol (plus an optional revision id).
 */
export type GetOwnedAppByComponentKeyRequest = {
  projectName: string;
  developerSymbol: string;
  revisionId: Default<string>;
  applicationRequestExtras: Default<ApplicationRequestExtras>;
};

export type LocalisedAppPermission = {
  appPermissionId: number;
  name: string;
  icon?: string;
  shortDescription: string;
  longDescription: string;
  readOnly?: boolean;
};

export type CausalChainLink = {
  coordinates: ChirpMethodCoordinates;
  errorClass: string;
  errorMessage?: string;
};

export type ChirpMethodCoordinates = { service: string; method: string };

export type EmptyError = {
  chirpErrorMessage?: string;
  coordinates?: ChirpMethodCoordinates;
  causalChain: Default<CausalChainLink[]>;
  shouldRetry?: boolean;
};

export type AppPermissionType =
  | 'REQUIRED'
  | 'ADDITIONAL'
  | 'OPTIONAL'
  | unhandledEnumCase;

export type GetAppComponentIndexByAppIdRequestType =
  Requestify<AppComponentIndexByAppIdRequest>;
export type GetAppComponentIndexByAppIdResponseType = AppComponentIndexResponse;
export type GetAppComponentIndexByAppIdErrorType = ApplicationError &
  HubspotMetadata;
export const getAppComponentIndexByAppIdRpc: ChirpAvroRpcDetails<
  GetAppComponentIndexByAppIdRequestType,
  GetAppComponentIndexByAppIdResponseType,
  GetAppComponentIndexByAppIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getAppComponentIndexByAppId',
  auth: 'app',
  fingerprint: 'zfOlZmbh4S4jB_guqaBV756EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetApplicationsForPortalRequestType =
  Requestify<ApplicationsByPortalRequest>;
export type GetApplicationsForPortalResponseType = ApplicationsByPortalResponse;
export type GetApplicationsForPortalErrorType = ApplicationError &
  HubspotMetadata;
export const getApplicationsForPortalRpc: ChirpAvroRpcDetails<
  GetApplicationsForPortalRequestType,
  GetApplicationsForPortalResponseType,
  GetApplicationsForPortalErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getApplicationsForPortal',
  auth: 'app',
  fingerprint: 'thFeD6ZoNWvdxPdXw23J8p6EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetApplicationsByProjectAndPortalRequestType =
  Requestify<ApplicationsByProjectRequest>;
export type GetApplicationsByProjectAndPortalResponseType =
  ApplicationsByProjectAndPortalResponse;
export type GetApplicationsByProjectAndPortalErrorType = EmptyError &
  HubspotMetadata;
export const getApplicationsByProjectAndPortalRpc: ChirpAvroRpcDetails<
  GetApplicationsByProjectAndPortalRequestType,
  GetApplicationsByProjectAndPortalResponseType,
  GetApplicationsByProjectAndPortalErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getApplicationsByProjectAndPortal',
  auth: 'app',
  fingerprint: '7D9fzzy5B7n5egHcRdC-65wP6LXzGzX8',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetByClientIdRequestType = Requestify<ApplicationByClientIdRequest>;
export type GetByClientIdResponseType = ApplicationResponse;
export type GetByClientIdErrorType = ApplicationError & HubspotMetadata;
export const getByClientIdRpc: ChirpAvroRpcDetails<
  GetByClientIdRequestType,
  GetByClientIdResponseType,
  GetByClientIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getByClientId',
  auth: 'app',
  fingerprint: 'oYFlgwNwQlv43Etx-LLB7p6EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetByIdRequestType = Requestify<ApplicationByIdRequest>;
export type GetByIdResponseType = ApplicationResponse;
export type GetByIdErrorType = ApplicationError & HubspotMetadata;
export const getByIdRpc: ChirpAvroRpcDetails<
  GetByIdRequestType,
  GetByIdResponseType,
  GetByIdErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getById',
  auth: 'app',
  fingerprint: 'eppiLZXsBaf43Etx-LLB7p6EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetAppByComponentKeyRequestType =
  Requestify<GetOwnedAppByComponentKeyRequest>;
export type GetAppByComponentKeyResponseType = ApplicationResponse;
export type GetAppByComponentKeyErrorType = ApplicationError & HubspotMetadata;
export const getAppByComponentKeyRpc: ChirpAvroRpcDetails<
  GetAppByComponentKeyRequestType,
  GetAppByComponentKeyResponseType,
  GetAppByComponentKeyErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getAppByComponentKey',
  auth: 'app',
  fingerprint: '4SGKiCxxAQn43Etx-LLB7p6EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetByIdsRequestType = Requestify<ApplicationsByIdsRequest>;
export type GetByIdsResponseType = ApplicationsByIdsResponse;
export type GetByIdsErrorType = ApplicationError & HubspotMetadata;
export const getByIdsRpc: ChirpAvroRpcDetails<
  GetByIdsRequestType,
  GetByIdsResponseType,
  GetByIdsErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getByIds',
  auth: 'app',
  fingerprint: 'o6rlVzmA-teq8XZMLlMqK56EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetAiConnectorCatalogRequestType =
  Requestify<AiConnectorCatalogRequest>;
export type GetAiConnectorCatalogResponseType = AiConnectorCatalogResponse;
export type GetAiConnectorCatalogErrorType = ApplicationError & HubspotMetadata;
export const getAiConnectorCatalogRpc: ChirpAvroRpcDetails<
  GetAiConnectorCatalogRequestType,
  GetAiConnectorCatalogResponseType,
  GetAiConnectorCatalogErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getAiConnectorCatalog',
  auth: 'app',
  fingerprint: '_yHDXGksR777phBdz2eH5Z6EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};

export type GetLocalisedAppPermissionsRequestType =
  Requestify<GetLocalisedAppPermissionsRequest>;
export type GetLocalisedAppPermissionsResponseType =
  GetLocalisedAppPermissionsResponse;
export type GetLocalisedAppPermissionsErrorType = ApplicationError &
  HubspotMetadata;
export const getLocalisedAppPermissionsRpc: ChirpAvroRpcDetails<
  GetLocalisedAppPermissionsRequestType,
  GetLocalisedAppPermissionsResponseType,
  GetLocalisedAppPermissionsErrorType,
  false
> = {
  serviceName: 'com.hubspot.applications.rpc.ApplicationsRpc',
  rpcName: 'getLocalisedAppPermissions',
  auth: 'app',
  fingerprint: 'vqizbSwz5lBlPrKLuON8w56EA-yclie3',
  na1Only: false,
  isStreaming: false,
  isHublessAppAuth: false,
};
